import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as DefaultVehicleIcon } from '../../../../assets/icons/car.svg';
import { ReactComponent as NavigationIcon } from '../../../../assets/icons/navigation-icon.svg';
import { ReactComponent as Power } from '../../../../assets/icons/power.svg';
// import { brandIcons } from '../../../../config/branding';
import { useAppBranding } from '../../../../config/branding';
import { useWebSocket } from '../../../../contexts/WebSocketV2/WebSocketProvider';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import SelectField from '../../../../shared-components/select-field/SelectField';
import stationManagement from '../../../../utils/api/station-management';

/**
 * Format minutes into "Hh M min" or "M min"
 * No seconds, no rounding.
 */
const formatMinutes = (totalMinutes: number, t: any): string => {
  if (!totalMinutes || totalMinutes <= 0) return `0 ${t('min') || 'min'}`;

  const total = Math.floor(totalMinutes); // remove decimal completely

  if (total < 60) {
    return `${total} ${t('min') || 'min'}`;
  }

  const hours = Math.floor(total / 60);
  const minutes = total % 60;

  return minutes > 0
    ? `${hours}${t('hr') || 'h'} ${minutes} ${t('min') || 'min'}`
    : `${hours}${t('hr') || 'h'}`;
};

/**
 * Live logs session component
 * @returns {any} The rendered component
 */
function LiveLogsSession() {
  const { t, i18n } = useTranslation('sidePanel');
  const branding = useAppBranding();

  const CurrencyIcon = branding.icons.currencyIcon;

  const defaultVehicle = useMemo(
    () => ({
      brand: {
        logo: '',
        name: '-',
        modal: '-',
        image: '',
      },
      power: [
        { id: 1, icon: Power, label: t('liveWatts'), value: '0 kW' },
        { id: 2, icon: CurrencyIcon, label: t('liveCost'), value: `0 ${branding.text.currency}` },
        { id: 3, icon: CurrencyIcon, label: t('chargeCostPerKw'), value: `0 ${branding.text.currency}` },
      ],
      data: [
        { label: t('status'), value: '-' },
        { label: t('kwh'), value: '0' },
        { label: t('chargePercentage'), value: '0' },
        { label: t('connector'), value: '-' },
        { label: t('voltage'), value: '0' },
        { label: t('estTime'), value: `0 ${t('min')}` },
      ],
    }),
    [t, CurrencyIcon, branding.text.currency],
  );

  const dispatch = useAppDispatch();

  const { connectWebSocket } = useWebSocket();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedDevice, setSelectedDevice] = useState<string | number>('');
  const [deviceOptions, setDeviceOptions] = useState<{ label: string; value: string }[]>([]);
  const { selectedChargingPoint } = useAppSelector((state) => state.stationManagement);
  const { messages, currentMessage } = useAppSelector((state) => state.ocppMessageV2);
  const [vehicleInfoList, setVehicleInfoList] = useState<any[]>([]);

  /**
   * Handle dropdown change
   */
  const handleChangeDevice = (_name: string, sn: string | number | (string | number)[]) => {
    const selected = Array.isArray(sn) ? sn[0] : sn;
    setSelectedDevice(selected);
  };
  /**
   *  connect web Socket
   */
  const handleConnect = () => {
    if (selectedDevice) {
      connectWebSocket(selectedDevice); // automatically disconnects old and connects new
    }
  };

  /**
   * Fetch device list
   */
  const handleDevicesList = async () => {
    if (!selectedChargingPoint) return;

    try {
      const response = await stationManagement.fetchDeviceList({
        chargingStation: selectedChargingPoint?.id,
      });

      const list = response.data || [];
      const formatted = list.map((item: any) => ({
        label: item.chargingPointName || item.chargingPointSn || t('unnamedDevice'),
        value: item.chargingPointSn,
      }));

      setDeviceOptions(formatted);

      // Auto-select first device
      if (formatted.length > 0) {
        handleChangeDevice('value', formatted[0].value);
      }
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || t('somethingWentWrong'),
          },
        },
      });
    }
  };

  /**
   * Fetch vehicle information for the current device
   */
  const handleVehicleInformation = async () => {
    if (!selectedDevice) return;

    try {
      const response = await stationManagement.fetchVehicleInformation({
        chargingStation: selectedChargingPoint?.id,
        chargingPointSn: selectedDevice,
      });
      setVehicleInfoList(response.data?.result || []);
    } catch (error) {
      console.error('Error fetching vehicle info:', error);
    }
  };

  /**
   * Convert websocket messages into connector slides
   */
  const formattedLiveData = messages
    .filter((message: any) => message.event === 'RealTime Charging Monitoring')
    .map((message: any) => ({
      connector: message.connectorId || '-',
      energyConsumedWh: message.energyConsumedWh / 1000 || 0,
      status: message.status || '-',
      estTime: message.estTime?.minutes
        ? formatMinutes(message.estTime.minutes, t)
        : `0 ${t('min')}`,

      totalKwhConsumed: message.totalKwhConsumed || 0,
      newChargingPercentage:
        message.socVsKW?.soc !== undefined
          ? Math.round(message.socVsKW.soc)
          : message.newChargingPercentage || 0,
      chargingSpeed: message.chargingSpeed || 0,
      pricePerKWh: message.pricePerKWh || 0,
      totalAmount: message.liveCostWithPlatformFee || 0,
      vehicle: {
        manufacturer: message.vehicle?.manufacturer || '-',
        model: message.vehicle?.model || '-',
        connectorType: message.vehicle?.connectorType || '-',
        image: '',
      },
    }));

  /**
   * Enrich live data with fetched vehicle information
   */
  const enrichedLiveData = formattedLiveData.map((liveMessage) => {
    const info = vehicleInfoList.find((v) => v.connector === liveMessage.connector);
    if (info) {
      return {
        ...liveMessage,
        vehicle: {
          ...liveMessage.vehicle,
          manufacturer: info.vehicle?.manufacturer || liveMessage.vehicle.manufacturer,
          model: info.vehicle?.model || liveMessage.vehicle.model,
          image: info.vehicleImageUrl || '',
        },
      };
    }
    return liveMessage;
  });

  // {
  //       event: 'RealTime Charging Monitoring',
  //       status: 'Charging inprogress',
  //       estTime: {
  //         minutes: estTimeInMinutes,
  //       },
  //       totalKwhConsumed,
  //       newChargingPercentage,
  //       chargingSpeed,
  //       pricePerKWh,
  //       totalAmount,
  //       connector: body?.chargingTransaction?.connector,
  //       vehicle: body?.chargingTransaction?.vehicle,
  //     }
  const currentLiveSlide = enrichedLiveData[currentIndex];

  /**
   * Navigation handlers
   */
  const handlePrevious = () => {
    setCurrentIndex((previous) => (previous === 0 ? formattedLiveData.length - 1 : previous - 1));
  };

  /**
   * Navigation handlers
   */
  const handleNext = () => {
    setCurrentIndex((previous) => (previous === formattedLiveData.length - 1 ? 0 : previous + 1));
  };

  /**
   * APPLY LIVE DATA → BUILD VEHICLE UI OBJECT
   */
  const liveVehicle = currentLiveSlide && {
    brand: {
      logo: currentLiveSlide.vehicle.manufacturer?.logo || '',
      name:
        typeof currentLiveSlide.vehicle.manufacturer === 'object'
          ? currentLiveSlide.vehicle.manufacturer.name || '-'
          : currentLiveSlide.vehicle.manufacturer || '-',
      modal: currentLiveSlide.vehicle.model || '-',
      image:
        currentLiveSlide.vehicle.image && currentLiveSlide.vehicle.image !== ''
          ? currentLiveSlide.vehicle.image
          : '', // fallback if no image
    },
    power: [
      {
        id: 1,
        icon: Power,
        label: t('liveWatts'),
        value: `${currentLiveSlide.energyConsumedWh} kW`,
      },
      {
        id: 2,
        icon: branding.icons.currencyIcon,
        label: t('liveCost'),
        value: `${currentLiveSlide.totalAmount} ${branding.text.currency}`,
      },
      {
        id: 3,
        icon: branding.icons.currencyIcon,
        label: t('pricePerKWh'),
        value: `${currentLiveSlide.pricePerKWh} ${branding.text.currency}`,
      },
    ],
    data: [
      { label: t('status'), value: currentLiveSlide.status },
      { label: t('kwh'), value: currentLiveSlide.totalKwhConsumed },
      { label: t('chargePercentage'), value: `${currentLiveSlide.newChargingPercentage}%` },
      { label: t('connector'), value: currentLiveSlide.connector },
      { label: t('voltage'), value: currentLiveSlide.chargingSpeed },
      { label: t('estTime'), value: currentLiveSlide.estTime },
    ],
  };

  const displayedVehicle = liveVehicle || defaultVehicle;

  useEffect(() => {
    handleDevicesList();
  }, [selectedChargingPoint]);
  useEffect(() => {
    handleConnect();
    handleVehicleInformation();
  }, [selectedDevice]);

  console.log(messages, currentMessage, 'messages');

  return (
    <div className="live-logs-session-wrap" dir={i18n.dir()}>
      {/* HEADER */}
      <div className="live-logs-session-header">
        <SelectField
          name="value"
          options={deviceOptions}
          selectedValue={selectedDevice}
          onChangeValue={handleChangeDevice}
        />
      </div>

      {/* CONTENT */}
      <div className="live-logs-session-content">
        {/* LEFT SIDE */}
        <div className="live-logs-session-left-content">
          <div className="content-top">
            <div className="brand-logo">
              <img
                src={displayedVehicle.brand.logo}
                alt={displayedVehicle.brand.name}
                className="vehicle-logo"
              />
            </div>
            <div className="brand-details">
              <p>{displayedVehicle.brand.name}</p>
              <p>{displayedVehicle.brand.modal}</p>
            </div>
          </div>

          <div className="content-middle">
            {displayedVehicle.power.map((data) => {
              const Icon = data.icon as React.FC<React.SVGProps<SVGSVGElement>>;
              return (
                <div className="charging-details" key={data.id}>
                  <p className="charging-details-label">{data.label}</p>
                  <div className="charging-details-desc">
                    <Icon className="icons" />
                    <p className="value">{data.value}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="bottom-line" />
          <div className="bottom-content">
            {displayedVehicle.data.map((details) => (
              <div className="info" key={details.label}>
                <p>{details.label} :</p>
                <p>{details.value}</p>
              </div>
            ))}
          </div>
        </div>

        {/* RIGHT SIDE */}
        <div className="live-logs-session-right-content">
          <div className="live-logs-session-car-container">
            <div className="live-logs-session-car">
              {displayedVehicle.brand.image ? (
                <img src={displayedVehicle.brand.image} alt="Car" />
              ) : (
                <span className="img">
                  <DefaultVehicleIcon />
                </span>
              )}
              <div className="device-status-container online">
                <div className="static-container" />
                <div className="animation-container" />
              </div>
            </div>
            <div className="live-logs-session-device-navigation">
              <NavigationIcon className="left-navigation" onClick={handlePrevious} />
              <NavigationIcon className="right-navigation" onClick={handleNext} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LiveLogsSession;
